import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "src/store/store";

interface UseAutoCaptureProps {
  streamingMode: boolean;
  captureCallback: () => void;
  isDialogOpen: boolean;
}

interface UseAutoCaptureReturn {
  isAutoCapturing: boolean;
}

/**
 * Custom hook to manage auto-capture logic in streaming mode
 * Handles timing and visual effects for automatic frame capture
 */
export const useAutoCapture = ({
  streamingMode,
  captureCallback,
  isDialogOpen,
}: UseAutoCaptureProps): UseAutoCaptureReturn => {
  const { streamingGuidanceMessage, imageNextStage } = useAppSelector(
    (state) => state.inspectionCapture
  );
  const [isAutoCapturing, setIsAutoCapturing] = useState(false);
  
  // Track auto-capture state
  const autoCaptureInProgress = useRef(false);
  const lastAutoCaptureTime = useRef(0);

  // Auto-capture disabled - user must manually click capture button
  // This prevents unwanted analyze-evidence-async API calls
  useEffect(() => {
    // Auto-capture is disabled - do nothing
    // User will manually click the capture button when ready
  }, [streamingGuidanceMessage, imageNextStage, streamingMode, captureCallback, isDialogOpen]);

  // Visual effect for auto-capture
  useEffect(() => {
    if (imageNextStage === 'HIGH_RES' && streamingMode) {
      setIsAutoCapturing(true);

      const resetTimer = setTimeout(() => {
        setIsAutoCapturing(false);
      }, 1000);

      return () => clearTimeout(resetTimer);
    }
  }, [imageNextStage, streamingMode]);

  return {
    isAutoCapturing,
  };
};
